import {
  Backdrop,
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import Header from '../../components/Header';
import StatsCard from '../../components/StatsCard';
import CustomTable from '../../components/CustomTable';
import { greenColor } from '../../config/colors';
import { tokens } from '../../theme';
import { useCookiesLogin } from '../../config/cookies';
import api from '../../services/api';
import { Building2, ClipboardList, SearchCheck } from 'lucide-react';

const BADGE_COLORS = {
  1: '#1CCA5B',
  2: '#6B7280',
  3: '#9CA3AF',
};

const RankingBadge = ({ position }) => (
  <Box
    sx={{
      width: 32,
      height: 32,
      borderRadius: '50%',
      backgroundColor: BADGE_COLORS[position] || '#D1D5DB',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '13px', lineHeight: 1 }}>
      {position}
    </Typography>
  </Box>
);

const buildRankingColumns = (colors) => [
  {
    field: 'position',
    headerName: 'Posição',
    width: 90,
    render: (value) => <RankingBadge position={value} />,
  },
  {
    field: 'company_name',
    headerName: 'Nome da Revenda',
    render: (value) => (
      <Typography sx={{ fontWeight: 500, color: colors.grey[100], fontSize: '14px' }}>
        {value}
      </Typography>
    ),
  },
  {
    field: 'consults_count',
    headerName: 'Consultas realizadas',
    width: 180,
    render: (value) => (
      <Typography sx={{ color: colors.grey[200], fontSize: '14px' }}>
        {value?.toLocaleString('pt-BR') ?? '—'}
      </Typography>
    ),
  },
  {
    field: 'percentage',
    headerName: 'Percentual',
    width: 220,
    render: (value) => (
      <Box display="flex" alignItems="center" gap="12px">
        <LinearProgress
          variant="determinate"
          value={Math.min(parseFloat(value), 100)}
          sx={{
            flex: 1,
            height: 8,
            borderRadius: 4,
            backgroundColor: colors.grey[700],
            '& .MuiLinearProgress-bar': {
              backgroundColor: colors.greenAccent[500],
              borderRadius: 4,
            },
          }}
        />
        <Typography
          variant="body2"
          sx={{ minWidth: 44, fontWeight: 600, color: colors.grey[100], textAlign: 'right' }}
        >
          {value}%
        </Typography>
      </Box>
    ),
  },
];

const DashboardSuperAdmin = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { cookies } = useCookiesLogin();

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [data, setData] = useState({
    total_companies: 0,
    total_available_consults: 0,
    total_used_consults: 0,
    ranking: [],
  });

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setHasError(false);
      const { data: result } = await api.get('/v1/dashboard/super-admin');
      setData(result);
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!cookies?.AUTH_TOKEN) return;
    fetchData();
  }, [cookies?.AUTH_TOKEN, fetchData]);

  if (!cookies.AUTH_TOKEN || isLoading) {
    return (
      <Backdrop open style={{ zIndex: 9999 }}>
        <CircularProgress sx={{ color: greenColor }} />
      </Backdrop>
    );
  }

  if (hasError) {
    return (
      <Box m="20px">
        <Header
          title="Dashboard"
          subtitle="Visão geral das revendas e indicadores principais"
        />
        <Typography color="error" mt={2}>
          Erro ao carregar os dados. Tente novamente.
        </Typography>
      </Box>
    );
  }

  return (
    <Box m="20px">
      {/* ── Cabeçalho ── */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: '12px', md: 0 },
          mb: '12px',
          flexShrink: 0,
        }}
      >
        <Header
          title="Dashboard"
          subtitle="Visão geral das revendas e indicadores principais"
        />
      </Box>

      {/* ── Cards de indicadores ── */}
      <Box
        sx={{
          mb: '20px',
          display: 'grid',
          gap: '20px',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
          },
        }}
      >
        <StatsCard
          title="Total de revendas cadastradas"
          value={data.total_companies.toLocaleString('pt-BR')}
          icon={<Building2 />}
          showBorder={false}
        />
        <StatsCard
          title="Total de consultas disponíveis"
          value={data.total_available_consults.toLocaleString('pt-BR')}
          icon={<ClipboardList />}
          showBorder={false}
        />
        <StatsCard
          title="Total de consultas utilizadas"
          value={data.total_used_consults.toLocaleString('pt-BR')}
          icon={<SearchCheck />}
          showBorder={false}
        />
      </Box>

      {/* ── Ranking com fundo branco e sem sombra interna ── */}
      <Box sx={{ backgroundColor: '#fff', borderRadius: '8px', pt: '20px', px: '20px', pb: '4px' }}>
        <Typography
          variant="h5"
          sx={{ color: colors.grey[300], fontWeight: 700, mb: '12px' }}
        >
          Revendas por volume de consultas realizadas
        </Typography>

        <CustomTable
          columns={buildRankingColumns(colors)}
          rows={data.ranking}
          rowKey="position"
          rowsPerPageOptions={[5, 10, 25]}
          elevation={0}
        />
      </Box>
    </Box>
  );
};

export default DashboardSuperAdmin;
